// src/components/common/Forms/FormModal.tsx

import {useState, useCallback, useEffect, useMemo} from "react";
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {X, Save, Loader2} from "lucide-react";
import {Button, Modal} from "../../ui";
import FormField, {
  type CheckboxFieldProps,
  type CheckboxGroupFieldProps,
  type FileFieldProps,
  type FormFieldProps,
  type MultiSelectFieldProps,
  type NumberFieldProps,
  type RadioFieldProps,
  type SelectFieldProps,
  type SwitchFieldProps,
  type TextareaFieldProps,
  type TextFieldProps,
} from "./FormField";
import {clsx} from "clsx";
import {useToast} from "../../../hooks/common/useToast";
import {getFieldValue} from ".";

// Form validation types
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: unknown) => string | null;
}

export interface FormFieldConfig
  extends Omit<FormFieldProps, "onChange" | "value" | "error"> {
  validation?: ValidationRule;
  dependencies?: string[]; // Fields that this field depends on
  conditional?: (values: Record<string, unknown>) => boolean; // Show field conditionally
  placeholder?: string;
  helperText?: string;
}

interface FormModalProps {
  // Modal props
  isOpen: boolean;
  onClose: () => void;
  title: string;
  size?: "sm" | "md" | "lg" | "xl" | "full";

  // Form props
  fields?: FormFieldConfig[];
  initialValues?: Record<string, unknown>;
  onSubmit?: (values: Record<string, unknown>) => Promise<void> | void;
  submitText?: string;
  cancelText?: string;
  children?: React.ReactNode;

  // Loading & validation
  loading?: boolean;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;

  // Layout
  columns?: 1 | 2 | 3;
  spacing?: "compact" | "normal" | "relaxed";

  // Custom renderers
  headerRender?: () => React.ReactNode;
  footerRender?: (props: {
    onSubmit: () => void;
    onCancel: () => void;
    isValid: boolean;
    isSubmitting: boolean;
    values: Record<string, unknown>;
  }) => React.ReactNode;

  // Events
  onValuesChange?: (values: Record<string, unknown>) => void;
  onFieldChange?: (
    field: string,
    value: unknown,
    values: Record<string, unknown>
  ) => void;
}

const FormModal: React.FC<FormModalProps> = ({
  // Modal props
  isOpen,
  onClose,
  title,
  size = "lg",

  // Form props
  fields,
  initialValues = {},
  onSubmit,
  submitText = "Save",
  cancelText = "Cancel",
  children,

  // Loading & validation
  loading = false,
  validateOnChange = true,
  validateOnBlur = true,

  // Layout
  columns = 1,
  spacing = "normal",

  // Custom renderers
  headerRender,
  footerRender,

  // Events
  onValuesChange,
  onFieldChange,
}) => {
  const {error: showError, success} = useToast();

  // Form state
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize initial values to prevent unnecessary re-renders
  const memoizedInitialValues = useMemo(
    () => initialValues,
    [JSON.stringify(initialValues)]
  );

  // Reset form when modal opens/closes - FIXED VERSION
  useEffect(() => {
    if (isOpen) {
      setValues(memoizedInitialValues);
      setErrors({});
      setTouched({});
      setIsSubmitting(false);
    }
  }, [isOpen, memoizedInitialValues]);

  // Validation function
  const validateField = useCallback(
    (field: FormFieldConfig, value: unknown): string | null => {
      const {validation} = field;
      if (!validation) return null;

      // Required validation
      if (validation.required) {
        if (value === null || value === undefined || value === "") {
          return `${field.label || field.name} is required`;
        }
        if (Array.isArray(value) && value.length === 0) {
          return `${field.label || field.name} is required`;
        }
      }

      // Skip other validations if value is empty (not required)
      if (
        !validation.required &&
        (value === null || value === undefined || value === "")
      ) {
        return null;
      }

      // String validations
      if (typeof value === "string") {
        if (validation.minLength && value.length < validation.minLength) {
          return `${field.label || field.name} must be at least ${
            validation.minLength
          } characters`;
        }
        if (validation.maxLength && value.length > validation.maxLength) {
          return `${field.label || field.name} must be at most ${
            validation.maxLength
          } characters`;
        }
        if (validation.pattern && !validation.pattern.test(value)) {
          return `${field.label || field.name} format is invalid`;
        }
      }

      // Number validations
      if (typeof value === "number") {
        if (validation.min !== undefined && value < validation.min) {
          return `${field.label || field.name} must be at least ${
            validation.min
          }`;
        }
        if (validation.max !== undefined && value > validation.max) {
          return `${field.label || field.name} must be at most ${
            validation.max
          }`;
        }
      }

      // Custom validation
      if (validation.custom) {
        return validation.custom(value);
      }

      return null;
    },
    []
  );

  // Validate all fields
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      // Skip validation for conditional fields that are hidden
      if (field.conditional && !field.conditional(values)) {
        return;
      }

      const error = validateField(field, values[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fields, values, validateField]);

  // Handle field change
  const handleFieldChange = useCallback(
    (fieldName: string, value: unknown) => {
      const newValues = {...values, [fieldName]: value};
      setValues(newValues);

      // Mark field as touched
      setTouched((prev) => ({...prev, [fieldName]: true}));

      // Validate on change if enabled
      if (validateOnChange) {
        const field = fields.find((f) => f.name === fieldName);
        if (field) {
          const error = validateField(field, value);
          setErrors((prev) => ({
            ...prev,
            [fieldName]: error || "",
          }));
        }
      }

      // Call callbacks
      onValuesChange?.(newValues);
      onFieldChange?.(fieldName, value, newValues);
    },
    [
      values,
      fields,
      validateField,
      validateOnChange,
      onValuesChange,
      onFieldChange,
    ]
  );

  // Handle field blur
  const handleFieldBlur = useCallback(
    (fieldName: string) => {
      setTouched((prev) => ({...prev, [fieldName]: true}));

      // Validate on blur if enabled
      if (validateOnBlur) {
        const field = fields.find((f) => f.name === fieldName);
        if (field) {
          const error = validateField(field, values[fieldName]);
          setErrors((prev) => ({
            ...prev,
            [fieldName]: error || "",
          }));
        }
      }
    },
    [fields, values, validateField, validateOnBlur]
  );

  // Handle form submit
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (isSubmitting) return;

      // Validate entire form and get new errors directly
      const newErrors: Record<string, string> = {};

      fields?.forEach((field) => {
        // Skip validation for conditional fields that are hidden
        if (field.conditional && !field.conditional(values)) {
          return;
        }

        const error = validateField(field, values[field.name]);
        if (error) {
          newErrors[field.name] = error;
        }
      });

      const isValid = Object.keys(newErrors).length === 0;

      // Update errors state
      setErrors(newErrors);

      if (!isValid) {
        // Get specific validation error messages from newErrors
        const errorMessages = Object.values(newErrors).filter((msg) => msg);

        if (errorMessages.length > 0) {
          showError("Validation Failed", errorMessages.join(", "));
        } else {
          showError("Validation Error", "Please fix the errors and try again");
        }
        return;
      }

      setIsSubmitting(true);

      try {
        await onSubmit(values);
        success("Success", "Form submitted successfully");
        onClose();
      } catch (error) {
        console.error("Form submission error:", error);
        showError(
          "Submission Error",
          "Failed to submit form. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      fields,
      values,
      validateField,
      onSubmit,
      onClose,
      showError,
      success,
    ]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  // Check if form is valid
  const isValid = Object.values(errors).every((error) => !error);

  // Grid column classes
  const gridClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  };

  // Spacing classes
  const spacingClasses = {
    compact: "space-y-4",
    normal: "space-y-6",
    relaxed: "space-y-8",
  };

  // Filter visible fields based on conditional logic
  const visibleFields = (fields || []).filter(
    (field) => !field.conditional || field.conditional(values)
  );

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={title} size={size}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Custom header */}
        {headerRender && (
          <div className="border-b border-gray-200 dark:border-dark-700 pb-4">
            {headerRender()}
          </div>
        )}

        {/* Form fields */}
        {children ? (
          children
        ) : (
          <div
            className={clsx(
              "grid gap-6",
              gridClasses[columns],
              spacingClasses[spacing]
            )}
          >
            {visibleFields.map((field) => {
            // Handle different field types for onChange
            const handleChange = (value: unknown) => {
              handleFieldChange(field.name, value);
            };

            const handleBlur = () => {
              handleFieldBlur(field.name);
            };

            switch (field.type) {
              case "text":
              case "email":
              case "password":
              case "url":
              case "tel":
              case "textarea":
              case "date":
              case "datetime-local":
              case "time":
              case "richtext":
                return (
                  <FormField
                    key={field.name}
                    {...(field as TextFieldProps)}
                    value={getFieldValue(field as FormFieldProps, values)}
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(value: string) => handleChange(value)}
                    onBlur={handleBlur}
                  />
                );

              case "number":
                return (
                  <FormField
                    key={field.name}
                    {...(field as NumberFieldProps)}
                    value={getFieldValue(field as FormFieldProps, values)}
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(value: number | undefined) =>
                      handleChange(value)
                    }
                    onBlur={handleBlur}
                  />
                );

              case "select":
                return (
                  <FormField
                    key={field.name}
                    {...(field as SelectFieldProps)}
                    value={getFieldValue(field as FormFieldProps, values)}
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(value: string | number) => handleChange(value)}
                    onBlur={handleBlur}
                  />
                );

              case "radio":
                return (
                  <FormField
                    key={field.name}
                    {...(field as RadioFieldProps)}
                    value={getFieldValue(field as FormFieldProps, values)}
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(value: string | number) => handleChange(value)}
                    onBlur={handleBlur}
                  />
                );

              case "multiselect":
              case "checkbox-group":
                return (
                  <FormField
                    key={field.name}
                    {...(field as
                      | MultiSelectFieldProps
                      | CheckboxGroupFieldProps)}
                    value={getFieldValue(field as FormFieldProps, values)}
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(value: (string | number)[]) =>
                      handleChange(value)
                    }
                    onBlur={handleBlur}
                  />
                );

              case "checkbox":
                return (
                  <FormField
                    key={field.name}
                    {...(field as CheckboxFieldProps)}
                    checked={
                      getFieldValue(field as FormFieldProps, values) as boolean
                    }
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(checked: boolean) => handleChange(checked)}
                    onBlur={handleBlur}
                  />
                );

              case "switch":
                return (
                  <FormField
                    key={field.name}
                    {...(field as SwitchFieldProps)}
                    checked={
                      getFieldValue(field as FormFieldProps, values) as boolean
                    }
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(checked: boolean) => handleChange(checked)}
                    onBlur={handleBlur}
                  />
                );

              case "file":
                return (
                  <FormField
                    key={field.name}
                    {...(field as FileFieldProps)}
                    value={getFieldValue(field as FormFieldProps, values)}
                    error={touched[field.name] ? errors[field.name] ?? "" : ""}
                    disabled={field.disabled || loading || isSubmitting}
                    onChange={(files: File[]) => handleChange(files)}
                    onBlur={handleBlur}
                  />
                );

              default:
                return (
                  <div key={field.name} className="text-red-500">
                    Unsupported field type: {field.type}
                  </div>
                );
            }
          })}
          </div>
        )}

        {/* Form actions */}
        <div className="border-t border-gray-200 dark:border-dark-700 pt-4">
          {footerRender ? (
            footerRender({
              onSubmit: handleSubmit,
              onCancel: handleCancel,
              isValid,
              isSubmitting,
              values,
            })
          ) : (
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                {cancelText}
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={!isValid}>
                {submitText}
              </Button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default FormModal;
